<script setup>
/**
 * 「安全退出」页，对齐旧版 web/32.f248c671ff102c5229ee.js 里的
 * app-logout 组件（类 e、模板工厂 x、样式 s）。
 *
 * 旧版这一页的模板是**完全空白**的（x 返回 Jb(0,[],null,null)，没有任何 DOM），
 * 样式表也是空字符串。用户看到的只有 Ionic 的 loading 遮罩：
 *
 *   ngOnInit → logout()
 *     loading = loadingCtrl.create({ message: '正在退出，请稍等' }); loading.present()
 *     authService.logout() 为真 → loading.dismiss(); nav.navigateRoot('/login')
 *     否则                      → loading.dismiss()（留在这个空白页上）
 *
 * 注意 authService.logout() 是本地清 token 的同步方法，不发请求。
 *
 * 说明：Ionic loading 组件（md）的精确尺寸/圆角/阴影没有从旧版产物里逐条核对
 * （它在 web/113 里，取样需要按偏移读单行大文件）。这里按 md loading 的常见形态
 * 做了一个居中卡片 + 文案，形态一致但数值未经产物核对。
 */
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { logout } from '../services/auth'

const router = useRouter()
// 对应旧版的 loading 显隐；旧版 logout 失败时会关掉遮罩并停在空白页
const showLoading = ref(true)

onMounted(async () => {
  try {
    await logout()
    showLoading.value = false
    // 旧版 nav.navigateRoot('/login')：清空历史栈后进登录页
    router.replace('/login')
  } catch (_) {
    showLoading.value = false
  }
})
</script>

<template>
  <!-- 旧版这一页没有任何内容，所以正文保持空白 -->
  <div class="logout-page">
    <div v-if="showLoading" class="ion-loading">
      <div class="loading-backdrop"></div>
      <div class="loading-wrapper">
        <!-- 旧版 loadingCtrl 默认带一个 spinner，md 模式是 crescent（转圈的弧） -->
        <div class="loading-spinner"><span class="spinner-crescent"></span></div>
        <div class="loading-content">正在退出，请稍等</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 旧版样式表是空的，页面本身就是一块白底空白 */
.logout-page{position:fixed;top:0;left:0;width:100%;height:100%;background:#fff}

/* Ionic loading 遮罩的形态还原（数值未从旧版产物核对，见文件头说明） */
.ion-loading{position:fixed;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;z-index:20000}
.loading-backdrop{position:absolute;top:0;left:0;width:100%;height:100%;background:#000;opacity:.32}
.loading-wrapper{
  position:relative;z-index:10;display:flex;align-items:center;
  max-width:270px;padding:24px;border-radius:2px;
  background:var(--ion-overlay-background-color,#fff);color:#000;
  box-shadow:0 11px 15px -7px rgb(0 0 0 / 20%),0 24px 38px 3px rgb(0 0 0 / 14%),0 9px 46px 8px rgb(0 0 0 / 12%);
  font:14px Arial,"Microsoft YaHei",sans-serif;
}
.loading-spinner{margin-right:16px;display:flex;align-items:center}
/* md 默认 spinner 是 crescent：一段 4px 宽的圆弧在转 */
.spinner-crescent{
  display:block;width:28px;height:28px;border-radius:50%;
  border:4px solid var(--legacy-theme,#f95454);border-right-color:transparent;border-bottom-color:transparent;
  animation:spinner-rotate 1s linear infinite;
}
@keyframes spinner-rotate{from{transform:rotate(0)}to{transform:rotate(1turn)}}
.loading-content{font-weight:400}
</style>
